import { useState } from "react";

import { Box, Button, Column, Dialog, Row, Text } from "@hightouch/htui";
import { compositeResourceIdCodec } from "@hightouch/lib/lineage/graph/extractors/identity";

import { ColumnUsageCell } from "src/components/lineage/column-usage";
import { DependencyTree } from "src/components/lineage/dependency-tree";
import { useDependentsCounts } from "src/components/lineage/use-dependents-counts";
import { useIsUsageEnabled } from "src/components/lineage/use-is-usage-enabled";

type Props = {
  modelId: string;
  modelName: string;
  columnNames: string[];
  onReset: () => void;
};

const modelColumnResourceId = compositeResourceIdCodec(2);

export const DeleteColumnWarningModal = ({
  modelId,
  modelName,
  columnNames,
  onReset,
}: Props) => {
  const isUsageEnabled = useIsUsageEnabled();
  const [expandedColumn, setExpandedColumn] = useState<string | null>(null);

  const columns = columnNames.map((name) => ({
    name,
    resource: {
      resourceType: "model_column",
      resourceId: modelColumnResourceId.encode([modelId, name]),
    },
  }));
  const usage = useDependentsCounts({
    resources: columns.map(({ resource }) => resource),
  });

  const toggleExpanded = (columnName: string) => {
    setExpandedColumn((current) =>
      current === columnName ? null : columnName,
    );
  };

  return (
    <Dialog
      isOpen
      variant="info"
      headerIcon="danger"
      width="lg"
      title="Error saving model"
      actions={
        <Button variant="primary" onClick={onReset}>
          OK
        </Button>
      }
      onClose={onReset}
    >
      <Text>
        Your change to the model "<strong>{modelName}</strong>" would remove the
        following columns:
      </Text>
      <Column gap={2} my={8} pl={4}>
        {columns.map(({ name, resource }) => {
          const counts = usage.countsFor(resource);
          return (
            <Column key={name} gap={2}>
              <Row align="flex-start" gap={2} minWidth={0}>
                <Box // This is a bullet circle
                  bg="text.secondary"
                  borderRadius="full"
                  boxSize={1.5}
                  flexShrink={0}
                  mt={2}
                />
                <Box flex={1} minWidth={0}>
                  <Text fontWeight="semibold" wordBreak="break-word">
                    {name}
                  </Text>
                </Box>
                {isUsageEnabled && (
                  <ColumnUsageCell
                    columnName={name}
                    total={counts?.total ?? null}
                    isLoading={usage.isLoading}
                    isUnavailable={usage.isUnavailable}
                    onOpen={toggleExpanded}
                  />
                )}
              </Row>
              {isUsageEnabled && expandedColumn === name && counts !== null && (
                <DependencyTree
                  resourceType={resource.resourceType}
                  resourceId={resource.resourceId}
                />
              )}
            </Column>
          );
        })}
      </Column>
      <Text>
        At least one of those columns is used as a key in a model relationship,
        and saving this change would break that relationship. Please remove
        those columns from your model relationships before changing the model.
      </Text>
    </Dialog>
  );
};
